import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import { retrieveRawInitData, openTelegramLink } from "@telegram-apps/sdk";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { UserType } from "../types";

export default function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    
    fetch(`/api/users/${userId}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.user) {
          setUser(data.user);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching user:", error);
        setLoading(false);
      });
  }, [userId]);

  // Track analytics when user is loaded
  useEffect(() => {
    if (user && window.ym) {
      window.ym(103622962, "reachGoal", "view_user", { userId: user.username });
    }
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p>User not found</p>
        </div>
      </Layout>
    );
  }

  const initDataRaw = new URLSearchParams(retrieveRawInitData() ?? "");
  const owner = JSON.parse(initDataRaw.get("user") ?? "{}");

  const handleWrite = () => {
    if (window.ym) {
      window.ym(103622962, "reachGoal", "write_user", { userId: user.username });
    }
    openTelegramLink("https://t.me/" + user.username);
    // Implement the logic for writing a message to the user
  };

  const handleEditProfile = () => {
    fetch("/api/users/" + user.telegram_id + "/edit", { method: "POST" })
      .then(response => {
        if (!response.ok) {
          console.error("Failed to enable edit mode");
        }
      })
      .catch(error => {
        console.error("Error enabling edit mode:", error);
      });
    openTelegramLink("https://t.me/gsom_connector_bot");
  };

  const renderActionButton = () => {
    if (user.telegram_id === owner.id) {
      return (
        <Button
          variant="ghost"
          className="mt-4 bg-[#D33F44] hover:bg-[#B8353A] text-white border-none shadow-none"
          onClick={handleEditProfile}
        >
          Редактировать
        </Button>
      );
    } else {
      return (
        <Button
          variant="ghost"
          className="mt-4 bg-[#D33F44] hover:bg-[#B8353A] text-white border-none shadow-none"
          onClick={handleWrite}
        >
          Написать
        </Button>
      );
    }
  };

  return (
    <Layout
      showBackButton
      onBack={() => navigate(-1)}
      className="flex flex-col h-full"
    >
      <Card className="border-none bg-white text-black shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Avatar className="h-24 w-24">
              <AvatarImage
                src={`/api/users/${user.telegram_id}/avatar`}
                alt={user.first_name}
              />
            </Avatar>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {`${user.first_name} ${user.last_name}`}
          </CardTitle>
          <div>{user.employment_summary}</div>
          <div className="flex justify-center pl-1 pr-1">
            {renderActionButton()}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <ProfileItem label="Город" value={user.city} />
            <ProfileItem label="Группа" value={user.study_group} />
            <ProfileItem
              label="Услуги/экспертиза"
              value={user.expertise_offered}
            />
            <ProfileItem label="Индустрии" value={user.industries} />
            <ProfileItem label="Функциональные роли" value={user.positions} />
            <ProfileItem label="Интересный факт" value={user.random_fact} />
            <ProfileItem label="Хобби" value={user.hobbies} />
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
}

// Component for profile items
function ProfileItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}
